<template>
  <div class="confirm-modal">
    <!-- Formulaire de confirmation -->
    <div class="form-container">
      <!-- Message de confirmation -->
      <div class="form-field">
        <div class="confirmation-message">
          Vous êtes sur le point de confirmer cette livraison comme effectuée.
        </div>
      </div>
      
      <!-- Photo de l'emplacement -->
      <div class="form-field">
        <ShopProgressiveImageUpload
          v-model="locationPhoto"
          :delivery-id="delivery.id"
          title="Photo de confirmation"
          description="Prenez une photo pour confirmer la livraison"
          :required="true"
        />
        <small v-if="submitted && photoRequired && !locationPhoto" class="p-error">
          Une photo de confirmation est requise
        </small>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Textarea from 'primevue/textarea';
import ShopProgressiveImageUpload from './ShopProgressiveImageUpload.vue';
import axios from '@/api/axios';
import { useStore } from 'vuex'; // Import Vuex store

// Props
const props = defineProps({
  delivery: {
    type: Object,
    required: true
  },
  photoRequired: {
    type: Boolean,
    default: true
  }
});

// État du formulaire
const locationPhoto = ref('');
const submitted = ref(false);
const isSubmitting = ref(false);

// Store Vuex
const store = useStore();
// Vérifier si le mode hors ligne est actif
const isOnline = computed(() => store.getters['sync/isOnline']);
const isUserOfflineMode = computed(() => store.getters.isUserOfflineMode);

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  // Si la photo est requise, vérifier qu'elle existe
  if (props.photoRequired) {
    return !!locationPhoto.value;
  }
  
  // Sinon, le formulaire est toujours valide
  return true;
});

// Fonction pour gérer la confirmation
const handleConfirm = async () => {
  submitted.value = true;
  
  if (!isFormValid.value) {
    return { success: false, error: 'Photo requise' };
  }
  
  try {
    isSubmitting.value = true;
    
    // Préparer les données pour la mise à jour
    const updateData = {
      status: 'delivered',
      delivered: true
    };
    
    // Si une photo a été prise, l'inclure dans les données
    if (locationPhoto.value) {
      updateData.photo = locationPhoto.value;
      console.log('Photo incluse dans la mise à jour de livraison shop:', locationPhoto.value);
    }
    
    // Vérifier si en ligne ou hors ligne
    if (isOnline.value && !isUserOfflineMode.value) {
      // En ligne - Utiliser l'API directement
      const response = await axios.patch(
        `/livraison/shopDelivery/${props.delivery.id}/`, 
        updateData
      );
      
      // Préparer les données de retour
      const updatedDelivery = {
        ...props.delivery,
        ...response.data
      };
      
      return {
        success: true,
        updatedDelivery,
        locationPhoto: locationPhoto.value
      };
    } else {
      // Hors ligne ou mode hors ligne utilisateur
      // Utiliser le store pour gérer la synchronisation ultérieure
      await store.dispatch('sync/updateShopDelivery', {
        id: props.delivery.id,
        updates: updateData
      });
      
      // Créer une version mise à jour pour l'UI
      const updatedDelivery = {
        ...props.delivery,
        ...updateData
      };
      
      return {
        success: true,
        updatedDelivery,
        locationPhoto: locationPhoto.value,
        offlineMode: true
      };
    }
  } catch (error) {
    console.error('Erreur lors de la confirmation de livraison:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || 'Erreur inconnue' 
    };
  } finally {
    isSubmitting.value = false;
  }
};

// Exposer l'état de validité et la fonction de confirmation
defineExpose({ isFormValid, handleConfirm });
</script>

<style scoped>
.confirm-modal {
  width: 100%;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
}

.confirmation-message {
  background-color: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 0.375rem;
  padding: 1rem;
  color: #1E40AF;
  font-size: 0.9rem;
  line-height: 1.5;
}

.p-error {
  color: #EF4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}
</style> 